import React, { useState, useEffect, useCallback, useTransition } from "react";
import { useNavigate } from "react-router-dom";
import { Label, TextInput, Card, Toast, Dropdown } from "flowbite-react";
import { toast } from "sonner";
import { INVITE_BY_LINK } from "../../lib/urls";
import Table from "../../lib/ui/components/table/Table";
import Button from "../../lib/ui/components/button/Button";
import Spinner from "../../lib/ui/components/spinner/Spinner";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import Checkbox from "../../lib/ui/components/checkbox/Checkbox";


import {
  ME,
  COMPANY_MEMBERS,
  ROLES,
  UPDATE_PERMISSION,
  INVITE_PENDING,
  INVITE_ROLE_UPDATE,
  DELETE_INVITE,
  REMOVE_USER_FROM_COMPANY,
  UPDATE_COMPANY_NAME,
  UPDATE_COMPANY_DOMAIN,
  UPDATE_COMPANY_DEFAULT_INTERVIEW_TYPES,
  COMPANY_DIGEST_SETTINGS,
  DIGEST_SETTINGS,
} from "../../lib/urls";
import { parseInterviewTypes, ensureInterviewTypesArray, isVoiceFeatureEnabled, isInterviewTypePickerEnabled } from "../../utils/interviewTypes";
import ActionBar from "../../components/Settings/ActionBar";
import UserStatusDropdown from "../../components/Settings/UserStatusDropdown";
import ToggleField from "../../lib/ui/components/toggle-field/ToggleField";
import { useStore } from "../../store";
import { isDevEnvironment } from "../../utils/envUtils";
import { getDefaultInviteRole } from "./inviteRole";

const INTERNAL_ORG_IDS = ["3359b424-d63b-420f-baf0-658a0f5b685c", "fa01e29b-f6c3-4b08-b189-486efbd9069c"];

function SettingsPage() {
  const uid = useStore((state) => state.uid);
  const token = useStore((state) => state.token);
  const company = useStore((state) => state.company);
  const user = useStore((state) => state.user);
  const role = useStore((state) => state.role);
  const setCompany = useStore((state) => state.setCompany);
  const devModeEnabled = useStore((state) => state.devModeEnabled);
  const setDevModeEnabled = useStore((state) => state.setDevModeEnabled);
  const isAdmin = role === "admin" || role === "owner";
  const [isLoading, setIsLoading] = useState(false);
  const [companyDigestEnabled, setCompanyDigestEnabled] = useState(true);
  const [isDigestToggling, setIsDigestToggling] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [isRemovingUser, setIsRemovingUser] = useState(false);
  const [formData, setFormData] = useState({
    email: user?.email || "",
    name: user?.name || "",
    company: user?.company || "",
  });

  const [adminFormData, setAdminFormData] = useState({
    email: user?.email || "",
    companyName: company?.name || "",
    companyDomain: company?.company_domain || "",
    defaultInterviewTypes: ["TEXT"],
  });

  // Track original form data to detect changes
  const [originalAdminFormData, setOriginalAdminFormData] = useState({
    email: user?.email || "",
    companyName: company?.name || "",
    companyDomain: company?.company_domain || "",
    defaultInterviewTypes: ["TEXT"],
  });

  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(null);
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);

  const checkAdminStatus = useCallback(async () => {
    try {
      const response = await fetch(ME, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const company_data = data.company || {};
        const user_data = data.user || {};
        const defaultTypes = parseInterviewTypes(company_data.default_interview_types);
        
        const newAdminData = {
          email: user_data.email || "",
          companyName: company_data.name || "",
          companyDomain: company_data.company_domain || "",
          defaultInterviewTypes: defaultTypes,
        };
        setAdminFormData(newAdminData);
        setOriginalAdminFormData(newAdminData);

        setFormData((prevData) => ({
          ...prevData,
          name: user_data.name || "",
          email: user_data.email || "",
        }));

        setCompanyId(company_data.id);

        setCompany({ ...company, ...company_data });

        try {
          const digestRes = await fetch(DIGEST_SETTINGS, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (digestRes.ok) {
            const digestData = await digestRes.json();
            setCompanyDigestEnabled(digestData.company_digest_enabled !== false);
          }
        } catch (e) {
          console.error("Error loading digest settings:", e);
        }
      } else {
        console.error("Error checking admin status:", response.statusText);
      }
    } catch (error) {
      console.error("Error checking admin status:", error);
    }
  }, [uid]);

  const handleCompanyDigestToggle = async (checked) => {
    if (!company?.id || isDigestToggling) return;
    setIsDigestToggling(true);
    setCompanyDigestEnabled(checked);
    try {
      const res = await fetch(COMPANY_DIGEST_SETTINGS(company.id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ digest_enabled: checked }),
      });
      if (!res.ok) throw new Error("failed");
      toast.success(checked ? "Shortlist digests enabled" : "Shortlist digests disabled");
    } catch (e) {
      setCompanyDigestEnabled(!checked);
      toast.error("Failed to update digest setting");
    } finally {
      setIsDigestToggling(false);
    }
  };
  useEffect(() => {
    fetchCompanyMembers();
    fetchRoles();
    fetchPendingInvites();
    checkAdminStatus();
  }, [checkAdminStatus]);

  const fetchPendingInvites = async () => {
    try {
      const response = await fetch(INVITE_PENDING, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPendingInvites(data);
      } else {
        console.error("Error fetching pending invites:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching pending invites:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Company page only handles admin form data
    setAdminFormData({ ...adminFormData, [name]: value });
  };

  const handleInterviewTypeChange = (type, checked) => {
    const currentTypes = adminFormData.defaultInterviewTypes || [];
    let newTypes;
    if (checked) {
      newTypes = [...currentTypes, type];
    } else {
      newTypes = currentTypes.filter(t => t !== type);
    }
    setAdminFormData({ ...adminFormData, defaultInterviewTypes: newTypes });
  };

  const handleSave = async (user_id, role_id) => {
    setIsSaving(true);
    toast.info("Saving changes...");

    try {
      // Handle permission update
      if (user_id && role_id) {
        const result = await fetch(UPDATE_PERMISSION(user_id), {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            role_id: role_id,
          }),
        });
      }

      const nameChanged = adminFormData.companyName !== originalAdminFormData.companyName;
      const domainChanged = adminFormData.companyDomain !== originalAdminFormData.companyDomain;
      const adminTypes = ensureInterviewTypesArray(adminFormData.defaultInterviewTypes, []);
      const originalTypes = ensureInterviewTypesArray(originalAdminFormData.defaultInterviewTypes, []);
      const interviewTypesChanged = JSON.stringify([...adminTypes].sort()) !== JSON.stringify([...originalTypes].sort());

      if (nameChanged || domainChanged) {
        const nameUrl = UPDATE_COMPANY_NAME(
          companyId,
          adminFormData.companyName
        );
        const domainUrl = UPDATE_COMPANY_DOMAIN(
          companyId,
          adminFormData.companyDomain
        );

        const nameResponse = nameChanged ? await fetch(nameUrl, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }) : { ok: true };

        if (nameResponse.ok) {
          const domainResponse = domainChanged ? await fetch(domainUrl, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }) : { ok: true };

          if (domainResponse.ok) {
          } else {
            const data = await domainResponse.json();
            toast.error(`Failed to update company domain: ${data.detail}`);
            return;
          }
        } else {
          const data = await nameResponse.json();
          toast.error(`Failed to update company name: ${data.detail}`);
          return;
        }
      }

      if (interviewTypesChanged) {
        const adminTypes = ensureInterviewTypesArray(adminFormData?.defaultInterviewTypes, []);
        if (adminTypes.length === 0) {
          throw new Error("Please select at least one interview type");
        }
        const response = await fetch(
          UPDATE_COMPANY_DEFAULT_INTERVIEW_TYPES(companyId),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              default_interview_types: adminFormData.defaultInterviewTypes,
            }),
          }
        );
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || "Failed to update interview types");
        }
      }

      if (nameChanged || domainChanged || interviewTypesChanged) {
        toast.success("Settings saved successfully!");
        setOriginalAdminFormData({ ...adminFormData });
        if (nameChanged || domainChanged) {
          setCompany({
            ...company,
            name: adminFormData.companyName,
            company_domain: adminFormData.companyDomain,
          });
          checkAdminStatus();
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error(error.message || "An error occurred while updating the data.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setAdminFormData(originalAdminFormData);
  };

  // Check if form has been modified
  const hasChanges = () => {
    const adminTypes = ensureInterviewTypesArray(adminFormData.defaultInterviewTypes, []);
    const originalTypes = ensureInterviewTypesArray(originalAdminFormData.defaultInterviewTypes, []);
    return (
      adminFormData.companyName !== originalAdminFormData.companyName ||
      adminFormData.companyDomain !== originalAdminFormData.companyDomain ||
      JSON.stringify([...adminTypes].sort()) !== JSON.stringify([...originalTypes].sort())
    );
  };

  const canSave = () => {
    const adminTypes = ensureInterviewTypesArray(adminFormData.defaultInterviewTypes, []);
    return hasChanges() && adminTypes.length > 0;
  };

  const fetchCompanyMembers = async () => {
    setIsLoadingMembers(true);
    try {
      const members = await fetch(COMPANY_MEMBERS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!members.ok) {
        console.error("Error fetching company members:", members.statusText);
        return;
      }

      const membersData = await members.json();
      setMembers(membersData);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const fetchRoles = async () => {
    const roles = await fetch(ROLES, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!roles.ok) {
      console.error("Error fetching roles:", roles.statusText);
      return;
    }

    setRoles(await roles.json());
  };

  const handleMemberRoleChange = async (userId, roleName) => {
    const roleId = roles?.find((r) => r.name === roleName)?.id;
    if (!roleId) throw new Error("Role not found");
    const response = await fetch(UPDATE_PERMISSION(userId), {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ role_id: roleId }),
    });
    if (!response.ok) throw new Error("Failed to update role");
    fetchCompanyMembers();
    toast.success("Role updated successfully");
  };

  const inviteRoleChangeHandler = async (invite_token, roleName) => {
    const roleId = roles?.find((r) => r.name === roleName)?.id;
    if (!roleId) return;
    try {
      const r = await fetch(INVITE_ROLE_UPDATE(invite_token), {
        method: "PATCH",
        body: JSON.stringify({
          role_id: roleId,
        }),
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!r.ok) {
        throw new Error("Unexpected error occured");
      }
      toast.success("Role updated successfully");
      return r;
    } catch (error) {
      toast.error("Failed to update role");
      throw error;
    }
  };

  const copyLinkHandler = async (role_id) => {
    if (!role_id) {
      toast.error("The Recruiter role is unavailable. Refresh the page and try again.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(INVITE_BY_LINK(companyId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role_id: role_id,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const inviteLink =
          process.env.REACT_APP_FRONTEND_URL +
          "/register?inviteToken=" +
          data.token;
        await navigator.clipboard.writeText(inviteLink);
        toast.success("Copied link to clipboard.");
      } else {
        const data = await response.json().catch(() => null);
        const message = typeof data?.detail === "string"
          ? data.detail
          : "Unable to create the invite link.";
        throw new Error(message);
      }
    } catch (error) {
      console.error("Error creating invite link:", error);
      toast.error(error.message || "Unable to create the invite link.");
    } finally {
      setIsLoading(false);
    }
  };

  const removePendingInviteHandler = async (invite_token) => {
    try {
      const r = await fetch(DELETE_INVITE + invite_token, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!r.ok) {
        throw new Error("Unexpected error occured");
      }

      setPendingInvites((prev) =>
        prev.filter((invite) => invite.token !== invite_token)
      );
      toast.success("Invite removed successfully");
      return r;
    } catch (error) {
      toast.error("Failed to remove invite");
      throw error;
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    // Prevent users from deleting themselves
    if (uid === userId) {
      toast.error("You cannot delete your own account");
      return;
    }

    // Set user to remove and show confirmation modal
    setUserToRemove({ id: userId, name: userName });
    setShowRemoveConfirmation(true);
  };

  const confirmRemoveUser = async () => {
    if (!userToRemove) return;

    setIsRemovingUser(true);
    try {
      const url = REMOVE_USER_FROM_COMPANY(userToRemove.id, companyId);
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMembers((prevMembers) => prevMembers.filter((member) => member.user.id !== userToRemove.id));
        toast.success("User removed from company successfully!");
        fetchCompanyMembers();
        setShowRemoveConfirmation(false);
        setUserToRemove(null);
      } else {
        const data = await response.json();
        toast.error(`Failed to remove user: ${data.detail}`);
      }
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error("An error occurred while removing the user.");
    } finally {
      setIsRemovingUser(false);
    }
  };

  const cancelRemoveUser = () => {
    setShowRemoveConfirmation(false);
    setUserToRemove(null);
  };

  return (
    <div className="flex flex-col items-center p-8 bg-gray-100 min-h-screen">
      {isAdmin && (
        <ActionBar
          handleDiscard={handleDiscard}
          handleSave={handleSave}
          hasChanges={hasChanges()}
          canSave={canSave()}
          isSaving={isSaving}
        />
      )}

      <Card className="w-[1200px] h-auto">
        <div className="flex justify-between items-center mb-6">
          <h1
            className="text-h4 font-bold text-gray-800"
          >
            Admin Settings
          </h1>
          {company && INTERNAL_ORG_IDS.includes(company.id) && (
            <Button
              variant="secondary"
              onClick={() => navigate("/analytics")}
            >
              View Platform Analytics
            </Button>
          )}
        </div>
        {!isAdmin && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-small text-blue-700">
            You're viewing admin settings. Contact an admin to make changes.
          </div>
        )}
        <form className="flex flex-col gap-4">
          {/* Company Name Input */}
          <div>
            <Label htmlFor="companyName" className="mb-2 text-body-lg">
              Company Name
            </Label>
            <div className="mb-2"></div>
            <TextInput
              id="companyName"
              name="companyName"
              type="text"
              value={adminFormData.companyName || ""}
              onChange={handleChange}
              placeholder="Enter your company name"
              required
              disabled={!isAdmin}
            />
          </div>

          {/* Company Domain Input */}
          <div>
            <Label htmlFor="companyDomain" className="mb-2 text-body-lg">
              Company Domain
            </Label>
            <div className="mb-2"></div>
            <TextInput
              id="companyDomain"
              name="companyDomain"
              type="text"
              value={adminFormData.companyDomain || ""}
              onChange={handleChange}
              placeholder="Enter your company domain"
              required
              disabled={!isAdmin}
            />
          </div>
          {/* Shortlist digest master switch */}
          {isAdmin && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <ToggleField
                label="Shortlist digests"
                description="When off, no one at the company receives shortlist digest emails."
                checked={companyDigestEnabled}
                onChange={handleCompanyDigestToggle}
                disabled={isDigestToggling}
              />
            </div>
          )}
          {/* Developer mode (only visible in dev environment) */}
          {isDevEnvironment() && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <ToggleField
                id="devMode"
                label="Developer mode"
                description='When on, shows the "Send test message" section on applicant pages and allows opening applicants whose interview is still pending.'
                checked={devModeEnabled}
                onChange={setDevModeEnabled}
                disabled={!isAdmin}
              />
            </div>
          )}
          {/* Default Interview Types */}
          {isInterviewTypePickerEnabled() && (
          <div>
            <Label htmlFor="defaultInterviewTypes" className="mb-2 text-body-lg">
              Default Interview Types
            </Label>
            <div className="text-small text-gray-500 mb-3">
              Set the default interview types allowed for new jobs. You can override this setting for individual jobs.
            </div>
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={adminFormData.defaultInterviewTypes?.includes("TEXT") || false}
                  onChange={(e) => handleInterviewTypeChange("TEXT", e.target.checked)}
                  disabled={!isAdmin}
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-900">Text (SMS)</span>
                  <div className="text-small text-gray-500">
                    Candidates respond via text messages
                  </div>
                </div>
              </label>
              {isVoiceFeatureEnabled() && (
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={adminFormData.defaultInterviewTypes?.includes("VOICE") || false}
                  onChange={(e) => handleInterviewTypeChange("VOICE", e.target.checked)}
                  disabled={!isAdmin}
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-900">Voice</span>
                  <div className="text-small text-gray-500">
                    Candidates respond via voice calls
                  </div>
                </div>
              </label>
              )}
            </div>
            {ensureInterviewTypesArray(adminFormData.defaultInterviewTypes, []).length === 0 && (
              <p className="text-small text-red-500 mt-2">
                Please select at least one interview type
              </p>
            )}
          </div>
          )}
        </form>
        <div>
          <div className="flex justify-between mt-8">
            <h2 htmlFor="companyDomain" className=" text-body-lg">
              Members ({members?.length})
            </h2>
            {isAdmin && (
              <Button
                variant="tertiary"
                data-cy="add-users-button"
                onClick={() => copyLinkHandler(getDefaultInviteRole(roles)?.id)}
              >
                {isLoading ? ("Copying...") : ("Copy Invite Link")}
              </Button>
            )}
          </div>

          <Table className="border border-gray-200">
            <Table.Head className="border-b border-gray-200">
              <Table.HeadCell className="text-gray-500">Name</Table.HeadCell>
              <Table.HeadCell className="text-gray-500">
                Date Added
              </Table.HeadCell>
              <Table.HeadCell className="text-gray-500">Email</Table.HeadCell>
              <Table.HeadCell className="text-gray-500">Role</Table.HeadCell>
              <Table.HeadCell className="text-gray-500" />
            </Table.Head>
            <Table.Body>
              {members?.map((member) => (
                <Table.Row key={member.id} className="border-b">
                  <Table.Cell style={{ color: "black" }}>
                    <strong>{member.user.name}</strong>
                  </Table.Cell>
                  <Table.Cell>
                    {new Date(member.user.created_at).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                      }
                    )}
                  </Table.Cell>
                  <Table.Cell style={{ color: "black" }}>
                    <strong>{member.user.email}</strong>
                  </Table.Cell>
                  <Table.Cell>
                    <UserStatusDropdown
                      currentRole={member.role.name}
                      onRoleChange={(roleName) => handleMemberRoleChange(member.user.id, roleName)}
                      disabled={!isAdmin || uid === member.user.id || (role !== "owner" && member.role.name === "owner")}
                    />
                  </Table.Cell>
                  {isAdmin && (
                    <Table.Cell>
                      <Button
                        variant="danger"
                        onClick={() => handleRemoveUser(member.user.id, member.user.name)}
                        disabled={uid === member.user.id}
                        title={uid === member.user.id ? "You cannot delete your own account" : "Remove user"}
                      >
                        Remove
                      </Button>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
              {pendingInvites?.map((invite) => (
                <Table.Row key={invite.id} className="border-b">
                  <Table.Cell style={{ color: "black" }}>
                    <strong>Pending</strong>
                  </Table.Cell>
                  <Table.Cell>
                    {new Date(invite.created_at).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                      }
                    )}
                  </Table.Cell>
                  <Table.Cell style={{ color: "black" }}>
                    <strong>{invite.email}</strong>
                  </Table.Cell>
                  <Table.Cell>
                    <UserStatusDropdown
                      currentRole={roles?.find((r) => r.id === invite.role_id)?.name || "user"}
                      onRoleChange={(roleName) => inviteRoleChangeHandler(invite.token, roleName)}
                      disabled={!isAdmin}
                    />
                  </Table.Cell>
                  {isAdmin && (
                    <Table.Cell>
                      <Button
                        variant="danger"
                        onClick={() => removePendingInviteHandler(invite.token)}
                      >
                        Remove Invite
                      </Button>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}

              {isLoadingMembers ? (
                <Table.Row>
                  <Table.Cell colSpan={5} className="text-center py-8">
                    <Spinner size="lg" />
                  </Table.Cell>
                </Table.Row>
              ) : members?.length === 0 && pendingInvites?.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5} className="text-center py-4">
                    No users found.
                  </Table.Cell>
                </Table.Row>
              ) : null}
            </Table.Body>
          </Table>
        </div>
      </Card>

      <ConfirmationModal
        isOpen={showRemoveConfirmation}
        onClose={cancelRemoveUser}
        onConfirm={confirmRemoveUser}
        title="Remove User"
        message={`Are you sure you want to remove ${userToRemove?.name} from the company?`}
        details="This action will remove the user from the company but will not delete their account. They will no longer have access to this company."
        confirmButtonText="Remove User"
        confirmButtonVariant="danger"
        isLoading={isRemovingUser}
        loadingText="Removing user..."
      />
    </div>
  );
}

export default SettingsPage;
